import { useState } from "react";

import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";

import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { AssistantFab } from "@/components/AssistantFab";
import { BrandMark } from "@/components/BrandMark";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardShortcuts } from "@/components/KeyboardShortcuts";
import { NotificationsBell } from "@/components/NotificationsBell";
import { PlanBlancBanner } from "@/components/PlanBlancBanner";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { UserMenu } from "@/components/UserMenu";
import { usePermission } from "@/auth/usePermission";
import { NAV_SECTIONS, type NavItem } from "@/nav/sections";
import { useAuthStore } from "@/store/authStore";
import { useUIStore } from "@/store/uiStore";

const DRAWER_WIDTH_OPEN = 260;
const DRAWER_WIDTH_CLOSED = 72;
const BOTTOM_NAV_HEIGHT = 56;

// Phone bottom bar — the four screens a practitioner actually opens, plus an
// escape hatch to the full menu. Deliberately permission-free: every entry is
// a "/me" view any authenticated practitioner can reach.
const MOBILE_NAV = [
  { key: "myHome", to: "/my-home", icon: <HomeIcon /> },
  { key: "myPlanning", to: "/my-planning", icon: <CalendarViewMonthIcon /> },
  { key: "exchange", to: "/exchange", icon: <SwapHorizIcon /> },
  { key: "desiderata", to: "/desiderata", icon: <FavoriteIcon /> },
] as const;

// Nav sections live in @/nav/sections so the Home page can share them.


export function AppShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { drawerCollapsed, toggleDrawerCollapsed } = useUIStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Resolve gated permissions once up front so the filter below stays cheap
  // and React hook rules stay satisfied (no hooks inside .filter()).
  const canReadAudit = usePermission("audit:read");
  const canReadDocuments = usePermission("document:read_all");
  const canReadUsers = usePermission("user:read");
  const canCreateUsers = usePermission("user:create");
  const canReadBurnout = usePermission("burnout:read_all");
  const canEditPlanning = usePermission("planning:edit");
  const canReadTimesheets = usePermission("timesheet:read_all");
  const isGod = Boolean(useAuthStore((s) => s.me)?.is_god);
  const hasTenant = Boolean(useAuthStore((s) => s.me)?.tenant_id);
  const navItemAllowed = (item: NavItem): boolean => {
    if (item.requiresTenant && !hasTenant) return false;
    if (!item.permission) return true;
    if (item.permission === "__god__") return isGod;
    if (item.permission === "audit:read") return canReadAudit;
    if (item.permission === "document:read_all") return canReadDocuments;
    if (item.permission === "user:read") return canReadUsers;
    if (item.permission === "user:create") return canCreateUsers;
    if (item.permission === "burnout:read_all") return canReadBurnout;
    if (item.permission === "planning:edit") return canEditPlanning;
    if (item.permission === "timesheet:read_all") return canReadTimesheets;
    return true;
  };
  const navSections = NAV_SECTIONS.map((s) => ({
    id: s.id,
    items: s.items.filter(navItemAllowed),
  })).filter((s) => s.items.length > 0);

  const desktopDrawerWidth = drawerCollapsed ? DRAWER_WIDTH_CLOSED : DRAWER_WIDTH_OPEN;

  const drawerContent = (collapsed: boolean) => (
    <Box sx={{ width: collapsed ? DRAWER_WIDTH_CLOSED : DRAWER_WIDTH_OPEN, height: "100%", display: "flex", flexDirection: "column", overflowX: "hidden" }}>
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2,
          minHeight: 64,
        }}
      >
        {collapsed ? (
          <BrandMark variant="icon" height={36} />
        ) : (
          <BrandMark variant="wordmark" height={40} />
        )}
        {isDesktop && (
          <Tooltip title={collapsed ? t("nav.expand") : t("nav.collapse")} placement="right">
            <IconButton
              onClick={toggleDrawerCollapsed}
              size="small"
              aria-label={collapsed ? "expand drawer" : "collapse drawer"}
              sx={{ ml: collapsed ? 0 : 1 }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <List sx={{ flex: 1, py: 1, overflowY: "auto" }}>
        {navSections.map((section, sIdx) => (
          <Box key={section.id} component="li" sx={{ listStyle: "none" }}>
            {!collapsed && (
              <Typography
                variant="caption"
                sx={{
                  px: 2.5,
                  py: 0.5,
                  mt: sIdx === 0 ? 0 : 1,
                  display: "block",
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: "text.disabled",
                  fontSize: 11,
                }}
              >
                {t(`nav.section.${section.id}`)}
              </Typography>
            )}
            {collapsed && sIdx > 0 && (
              <Box sx={{ mx: 1.5, my: 1, borderTop: 1, borderColor: "divider" }} />
            )}
            {section.items.map((item) => {
              const label = t(`nav.${item.key}`);
              const isActive =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
              const button = (
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  selected={isActive}
                  sx={{
                    minHeight: 44,
                    px: collapsed ? 2 : 2.5,
                    justifyContent: collapsed ? "center" : "flex-start",
                    mx: 1,
                    borderRadius: 1.5,
                    mb: 0.25,
                    "&.Mui-selected": {
                      bgcolor: "action.selected",
                      "& .MuiListItemIcon-root": { color: "primary.main" },
                      "& .MuiListItemText-primary": { fontWeight: 600 },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? "auto" : 2,
                      justifyContent: "center",
                      color: "text.secondary",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary={label} />}
                </ListItemButton>
              );
              return (
                <Box key={item.key} component="div">
                  {collapsed ? (
                    <Tooltip title={label} placement="right">
                      <Box>{button}</Box>
                    </Tooltip>
                  ) : (
                    button
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100dvh" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          ml: { md: `${desktopDrawerWidth}px` },
          bgcolor: "background.paper",
          color: "text.primary",
          borderBottom: 1,
          borderColor: "divider",
          transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar>
          {!isDesktop && (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen((o) => !o)}
              sx={{ mr: 1 }}
              aria-label="open navigation"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t("app.tagline")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotificationsBell />
            <LocaleSwitcher />
            <ThemeSwitcher />
            <UserMenu />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Desktop drawer (mini-variant when collapsed) */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          width: desktopDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: desktopDrawerWidth,
            boxSizing: "border-box",
            borderRight: 1,
            borderColor: "divider",
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          },
        }}
      >
        {drawerContent(drawerCollapsed)}
      </Drawer>

      {/* Mobile drawer (always full width) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH_OPEN },
        }}
      >
        {drawerContent(false)}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${desktopDrawerWidth}px)` },
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <AnnouncementBanner />
        <PlanBlancBanner />
        {children}
        {/* Clears the fixed bottom bar (plus the iPhone home indicator). */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            height: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`,
          }}
        />
      </Box>

      {/* Phone-only bottom navigation */}
      <Paper
        elevation={8}
        sx={{
          display: { xs: "block", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (th) => th.zIndex.appBar,
          pb: "env(safe-area-inset-bottom)",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <BottomNavigation
          showLabels
          value={
            MOBILE_NAV.findIndex(
              (i) =>
                location.pathname === i.to || location.pathname.startsWith(`${i.to}/`),
            ) // -1 (no match) leaves every action unselected, which is correct
          }
          sx={{ height: BOTTOM_NAV_HEIGHT }}
        >
          {MOBILE_NAV.map((item) => (
            <BottomNavigationAction
              key={item.key}
              component={NavLink}
              to={item.to}
              label={t(`nav.${item.key}`)}
              icon={item.icon}
              sx={{ minWidth: 0, px: 0.5 }}
            />
          ))}
          <BottomNavigationAction
            label={t("nav.more")}
            icon={<MoreHorizIcon />}
            onClick={() => setMobileOpen(true)}
            sx={{ minWidth: 0, px: 0.5 }}
          />
        </BottomNavigation>
      </Paper>

      <CommandPalette />
      <KeyboardShortcuts />
      <AssistantFab />
    </Box>
  );
}
